import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '../components/PageContainer';

interface HelpSectionProps {
  title: string;
  children: ReactNode;
}

const HelpSection = ({ title, children }: HelpSectionProps) => {
  return (
    <section className="mt-8 first:mt-0">
      <h2 className="font-serif text-xl text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3 text-[15px] font-sans leading-relaxed text-gray-700">
        {children}
      </div>
    </section>
  );
};

const linkClasses = 'text-primary-700 hover:underline';

export const Help = () => {
  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer>
        <div className="max-w-3xl mx-auto py-6">
          <h1 className="font-serif text-3xl text-gray-900 mb-2">Help</h1>
          <p className="font-sans text-gray-600 mb-2">
            A guide to finding wineries, planning a visit, and getting the most out of
            WineryXplorer.
          </p>

          <HelpSection title="Finding wineries">
            <p>
              The{' '}
              <Link to="/wineries" className={linkClasses}>
                Wineries
              </Link>{' '}
              page is where you search for places to visit. Type the name of a winery — or a style
              of wine you enjoy — into the search box. To focus on a particular area, use the region
              and city filters, or select <strong>Use my location</strong> to find wineries near
              where you are now.
            </p>
            <p>
              When you browse on a computer, the Wineries page shows an interactive map beside your
              list of results. You can move the map to a different area and then select the
              search-this-area button to see only the wineries within the part of the map you are
              currently viewing. On a phone, select the map button to open that same map
              full-screen.
            </p>
            <p>
              To narrow your results further, use the category filters, which let you sort by the
              type of venue, its atmosphere, and the amenities it offers. You can also order your
              results by distance from you or alphabetically by name.
            </p>
            <p>
              If there is a search you return to often, save it using the saved-searches menu. You
              can re-run a saved search at any time, so you never have to set up the same filters
              twice.
            </p>
          </HelpSection>

          <HelpSection title="Tasting experiences and events">
            <p>
              The{' '}
              <Link to="/experiences" className={linkClasses}>
                Tasting Experiences
              </Link>{' '}
              page lists tastings you can book, organized by the winery that hosts them. Use the
              filters to narrow the list by price, by whether wine-club membership is required, and
              by each winery's reservation policy.
            </p>
            <p>
              The{' '}
              <Link to="/events" className={linkClasses}>
                Events
              </Link>{' '}
              page shows upcoming wine events, such as tastings, classes, and winemaker dinners. You
              can filter events by type, by venue, and by date range. Both the Tasting Experiences
              page and the Events page are organized by location, so start by choosing a region or
              city to see what is available there.
            </p>
          </HelpSection>

          <HelpSection title="Building an itinerary">
            <p>
              An itinerary is a plan for a day — or several days — of winery visits. To create one,
              open the{' '}
              <Link to="/itineraries" className={linkClasses}>
                Itineraries
              </Link>{' '}
              page, select the option to create a new itinerary, and give it a title.
            </p>
            <p>
              The itinerary editor lets you build your plan one day at a time. Add a day, and then
              add stops to it by searching for wineries in the venue picker. For each stop, you can
              set the time you plan to arrive, how long you expect to stay, and any notes you would
              like to remember. To change the order of your stops, drag them into place, or use your
              keyboard: press Space or Enter to pick up a stop, use the arrow keys to move it, and
              press Esc to cancel.
            </p>
            <p>
              You do not have to open the editor every time you want to add a winery. While you are
              browsing, open any winery's page and select the add-to-itinerary button near the top
              of the page. A short menu lists your existing itineraries — choose one to add the
              winery to it, or choose <strong>New itinerary</strong> to create one on the spot. If
              the itinerary you choose covers more than one day, you will then pick which day to add
              the winery to.
            </p>
            <p>
              As you plan, WineryXplorer estimates the driving time between each stop and warns you
              whenever a visit is scheduled to run past a winery's closing time, so you can adjust
              your plan before you set out. Your changes are saved automatically as you work.
            </p>
            <p>
              When your itinerary is ready, you can publish it as private, so that only you can see
              it, or as public. A public itinerary can be used as a starting template by other
              members planning their own trips.
            </p>
          </HelpSection>

          <HelpSection title="Making a reservation">
            <p>
              WineryXplorer does not book tastings on your behalf. Instead, every winery's listing
              tells you what kind of reservation it expects, so that you know how to plan your
              visit:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Reservation required</strong> — You must book in advance, because walk-in
                visitors are not accepted.
              </li>
              <li>
                <strong>Reservations recommended</strong> — Walk-in visitors are welcome, but
                booking ahead is a good idea.
              </li>
              <li>
                <strong>Drop-ins welcome</strong> — No reservation is needed.
              </li>
            </ul>
            <p>
              To make a reservation, open the page for the winery or tasting experience you are
              interested in, and use the booking link, website address, or phone number listed there
              to contact the winery directly. When a winery requires a reservation, be sure to book
              before you travel.
            </p>
          </HelpSection>

          <HelpSection title="Saving favorites">
            <p>
              To save a winery you would like to remember, select the heart icon. You will find it
              on the winery's own page and on each winery card in your search results. To save a
              wine, select the bookmark icon on that wine's listing.
            </p>
            <p>
              Everything you save is collected on your{' '}
              <Link to="/profile" className={linkClasses}>
                profile
              </Link>{' '}
              page: saved wineries appear on the Favorite Venues tab, and saved wines appear on the
              My Wines tab.
            </p>
          </HelpSection>

          <HelpSection title="Writing reviews">
            <p>
              To review a winery you have visited, open its page and select{' '}
              <strong>Write a Review</strong>. You will need to be signed in to your account. A
              review asks you to rate the winery in four areas — service and staff, the facility,
              the wines, and value — and your overall score is calculated from those four ratings.
              You then add a title, write your review, and, if you wish, attach photos.
            </p>
            <p>
              The reviews you have written, along with their current status, are listed on the
              Reviews tab of your profile page.
            </p>
          </HelpSection>

          <HelpSection title="Managing your account">
            <p>
              Your{' '}
              <Link to="/profile" className={linkClasses}>
                profile
              </Link>{' '}
              page is where you manage your account, and it is divided into several tabs. There you
              can edit your bio and personal details, look back over the reviews you have written,
              manage your wishlist of wines, and set your preferences for wines and regions. The
              Settings tab is where you change options such as your preferred distance unit (miles
              or kilometers), your phone number, and your password.
            </p>
          </HelpSection>

          <HelpSection title="Still need help?">
            <p>
              For tips on what to expect when you visit a winery, see the{' '}
              <Link to="/wine-tasting-faq" className={linkClasses}>
                Wine Tasting FAQ
              </Link>{' '}
              page. If you notice something inaccurate on a winery's listing, please tell us using
              the{' '}
              <Link to="/report-errors" className={linkClasses}>
                Feedback / Report Errors
              </Link>{' '}
              page.
            </p>
          </HelpSection>
        </div>
      </PageContainer>
    </div>
  );
};
